import { localDateToISO } from '@indeed/jspl-foundations';
import type { ScreenerQuestionContextQuestion } from '../../../../__generated__/graphql-base-types';
import {
    isNumericQuestion,
    normalizeNumericAnswer,
    type AnswerValue,
    type ScreenerFileAnswer,
} from '../screenerQuestionUtils';

/**
 * Known-harmless FILE drift on `ScreenerFileAnswer`. Persist identity is `name` + `fileToken`
 * (IAFE-3026); everything else is BE-only payload metadata or FE-only upload state.
 *
 *   - `contentType` / `contentHash` / `downloadHash` — always written by `toFileAnswer` from the
 *     save payload. A fresh `uploadFile` result is `{ name, fileToken, contentType? }` and never
 *     has the hashes; `contentType` may be omitted when the BFF does not echo it.
 *   - `uploading` / `error` — FE-only, never round-trip
 */
const FILE_DRIFT_KEYS = ['contentType', 'contentHash', 'downloadHash', 'uploading', 'error'];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const isFileAnswer = (value: unknown): value is ScreenerFileAnswer[] =>
    Array.isArray(value) &&
    value.some(
        (item) =>
            typeof item === 'object' && item !== null && 'name' in item && !Array.isArray(item),
    );

const isStringAnswer = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === 'string');

/** HTML 4.0 basic escape — the backend XSS filter's typical `" & < >` rewrite. */
const BASIC_ESCAPE: Record<string, string> = {
    '"': '&quot;',
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
};

const escapeHtml4 = (input: string): string => {
    let result = '';
    for (const char of input) {
        result += BASIC_ESCAPE[char] ?? char;
    }
    return result;
};

const normalizeTextAnswer = (answer: string): string => answer.trim().normalize('NFC');

const firstString = (value: string[] | undefined): string => value?.[0] ?? '';

const isBlankStringAnswer = (value: string[]): boolean =>
    value.length === 0 || value.every((item) => item.trim() === '');

/**
 * `getSavedAnswerValues` maps an empty text payload to `[]`, while the form can hold `['']`.
 * Same intent — copy the BE shape so the array-length diff is not a mismatch.
 */
const syncBlankStringAnswers = (
    answersMap: Record<string, AnswerValue>,
    questionName: string,
    backendAnswer: AnswerValue,
): boolean => {
    const frontendAnswer = answersMap[questionName];
    if (!isStringAnswer(frontendAnswer) || !isStringAnswer(backendAnswer)) return false;
    if (!isBlankStringAnswer(frontendAnswer) || !isBlankStringAnswer(backendAnswer)) return false;
    answersMap[questionName] = backendAnswer;
    return true;
};

const verifyAndUpdateFileFalsePositive = (
    frontendAnswer: AnswerValue | undefined,
    backendAnswer: AnswerValue | undefined,
): void => {
    if (!isFileAnswer(frontendAnswer) || !isFileAnswer(backendAnswer)) return;
    const minLength = Math.min(frontendAnswer.length, backendAnswer.length);
    for (let i = 0; i < minLength; i++) {
        const frontendFile = frontendAnswer[i];
        const backendFile = backendAnswer[i];
        if (frontendFile.uploading) continue;
        if (frontendFile.name !== backendFile.name) {
            // BE strips `/` from filenames (mosaic SQItvUtils).
            if (frontendFile.name.replace(/\//g, '') === backendFile.name) {
                frontendFile.name = backendFile.name;
            }
        }
    }
};

const verifyAndUpdateTextFalsePositive = (
    answersMap: Record<string, AnswerValue>,
    questionName: string,
    backendAnswer: AnswerValue,
): void => {
    const frontendAnswer = answersMap[questionName];
    if (!isStringAnswer(frontendAnswer) || !isStringAnswer(backendAnswer)) return;
    const fe = firstString(frontendAnswer);
    const be = firstString(backendAnswer);
    if (fe === be) return;

    const normalized = normalizeTextAnswer(fe);
    const escaped = normalizeTextAnswer(escapeHtml4(fe));
    if (normalized === be || escaped === be) {
        answersMap[questionName] = backendAnswer;
    }
};

const verifyAndUpdateDateFalsePositive = (
    question: ScreenerQuestionContextQuestion,
    answersMap: Record<string, AnswerValue>,
    locale: string,
    backendAnswer: AnswerValue,
): void => {
    const frontendAnswer = answersMap[question.name];
    if (!isStringAnswer(frontendAnswer) || !isStringAnswer(backendAnswer)) return;
    const fe = firstString(frontendAnswer);
    const be = firstString(backendAnswer);
    if (fe === be) return;

    const pattern = question.outputDatePattern ?? undefined;
    const feIso = localDateToISO(fe, locale, pattern);
    const beIso = localDateToISO(be, locale, pattern ?? 'yyyy-MM-dd');
    if (feIso && feIso === beIso) {
        answersMap[question.name] = backendAnswer;
    }
};

const verifyAndUpdateNumericFalsePositive = (
    question: ScreenerQuestionContextQuestion,
    answersMap: Record<string, AnswerValue>,
    backendAnswer: AnswerValue,
): void => {
    const frontendAnswer = answersMap[question.name];
    if (!isStringAnswer(frontendAnswer) || !isStringAnswer(backendAnswer)) return;
    const fe = firstString(frontendAnswer);
    const be = firstString(backendAnswer);
    if (fe === be) return;

    if (normalizeNumericAnswer(question, fe) === normalizeNumericAnswer(question, be)) {
        answersMap[question.name] = backendAnswer;
    }
};

/**
 * Reconciles known backend canonicalizations on the FE answer map so they don't surface as ITV
 * mismatches. Mutates `frontendAnswers` (the cloned FE side) to match BE when the difference is
 * HTML-escape/NFC, date padding, numeric separators, or FILE slash-stripping — mosaic SQItvUtils.
 */
export const updateFalsePositiveAnswersInApplicationData = (
    questions: ScreenerQuestionContextQuestion[],
    frontendAnswers: Record<string, AnswerValue>,
    backendAnswers: Record<string, AnswerValue>,
    locale: string,
): void => {
    const questionsByName = new Map(questions.map((question) => [question.name, question]));

    Object.entries(backendAnswers).forEach(([questionName, backendAnswer]) => {
        const question = questionsByName.get(questionName);
        if (!question) return;

        if (syncBlankStringAnswers(frontendAnswers, questionName, backendAnswer)) return;

        if (question.type === 'FILE') {
            verifyAndUpdateFileFalsePositive(frontendAnswers[question.name], backendAnswer);
        } else if (question.type === 'DATE') {
            verifyAndUpdateDateFalsePositive(question, frontendAnswers, locale, backendAnswer);
        } else if (question.type === 'TEXT' && isNumericQuestion(question)) {
            verifyAndUpdateNumericFalsePositive(question, frontendAnswers, backendAnswer);
        } else if (question.type === 'TEXT' || question.type === 'TEXTAREA') {
            verifyAndUpdateTextFalsePositive(frontendAnswers, question.name, backendAnswer);
        }
    });
};

const stripFileDriftKeysRec = (node: unknown): void => {
    if (!isPlainObject(node)) return;
    for (const key of FILE_DRIFT_KEYS) {
        delete node[key];
    }
    for (const key of Object.keys(node)) {
        const child = node[key];
        if (isPlainObject(child)) {
            stripFileDriftKeysRec(child);
            if (Object.keys(child).length === 0) {
                delete node[key];
            }
        }
    }
};

/** Strips known-harmless FILE fields from one slice of a `detailedDiff`. */
export const cleanFileAnswers = (innerDiff: Record<string, unknown>): void => {
    if (!isPlainObject(innerDiff.answers)) return;
    const answersDiff = innerDiff.answers;
    for (const answersKey of Object.keys(answersDiff)) {
        stripFileDriftKeysRec(answersDiff[answersKey]);
        if (
            isPlainObject(answersDiff[answersKey]) &&
            Object.keys(answersDiff[answersKey]).length === 0
        ) {
            delete answersDiff[answersKey];
        }
    }
    if (Object.keys(answersDiff).length === 0) {
        delete innerDiff.answers;
    }
};
